import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRODUCTS, PRICING } from "../data/products";
import { PRODUCT_SCENES } from "../data/productScenes";
import { JourneyBar } from "../components/JourneyBar";
import Product3DVisual from "../components/Product3DVisual";
import { buildWattzapOSUrl, openEnergyScanPortal } from "../lib/wattzapOS";
import { useT } from "../i18n/LanguageContext";

function ProductCard({ p }) {
  const t = useT();
  const Icon = p.icon;
  const priceLines = p.priceLines || (p.price ? [{ v: p.price }] : null);
  return (
    <div data-testid={`product-card-${p.slug}`} className="bg-white border border-slate-100 rounded-2xl p-7 shadow-card-soft hover:-translate-y-1 hover:shadow-cyan-glow/30 transition-all flex flex-col">
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF]">
          <Icon size={22} />
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-mist border border-slate-200 text-slate-600">{t(p.stage)}</span>
      </div>
      <h3 className="mt-5 font-heading text-xl text-graphite">{p.name}</h3>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t(p.promise)}</p>
      <p className="mt-3 text-sm text-slate-500 leading-relaxed"><span className="font-medium text-slate-600">{t("Solves:")}</span> {t(p.problem)}</p>
      <div className="mt-auto pt-6">
        {priceLines && (
          <div data-testid={`product-price-${p.slug}`} className="mb-4 pt-4 border-t border-slate-100 space-y-1.5">
            {priceLines.map((ln, i) => (
              <div key={i}>
                {ln.k && <div className="text-[10px] uppercase tracking-widest text-slate-400">{ln.k}</div>}
                {ln.v.includes("₹") ? (
                  <div className="font-heading text-base text-gradient-brand">{ln.v}</div>
                ) : (
                  <div className="text-sm font-medium text-slate-700">{ln.v}</div>
                )}
              </div>
            ))}
          </div>
        )}
        <Link to={p.path} data-testid={`product-card-link-${p.slug}`} className="inline-flex items-center gap-2 text-sm font-medium text-[#00D4FF] hover:gap-3 transition-all">
          {t("Explore")} {p.name} <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}

export default function Products() {
  const t = useT();
  return (
    <div data-testid="products-page">
      <section className="bg-[#071A2D] text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-3">{t("Products · Measured savings, not equipment sales")}</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{t("From energy diagnosis to verified savings")}</h1>
              <p className="mt-6 text-white/75 max-w-2xl">
                {t("WattZap helps industrial and commercial customers identify hidden electricity-cost losses, protect their bills, optimise operations, install correctly sized solar systems, access financed energy assets, and verify savings every month.")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="products-cta-primary" className="btn-gradient inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
                  {t("Start with a Free EnergyScan")} <ArrowRight size={16} />
                </a>
                <Link to="/journey" data-testid="products-cta-secondary" className="btn-outline-brand inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
                  {t("Explore the WattZap Journey")}
                </Link>
              </div>
            </div>
            <Product3DVisual scene={PRODUCT_SCENES["overview"]} testId="product-3d-overview" />
          </div>
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3">{t("The customer journey")}</div>
            <JourneyBar dark testId="products-journey-bar" />
          </div>
        </div>
      </section>

      {/* PRODUCT CARDS */}
      <section className="bg-mist py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.map((p) => <ProductCard key={p.slug} p={p} />)}
          </div>
          <p data-testid="products-recommendation-note" className="mt-10 text-sm text-slate-600 max-w-2xl">
            {t("Customers do not need to purchase every product. WattZap recommends only the products justified by measured customer data.")}
          </p>
        </div>
      </section>

      {/* INDICATIVE PRICING */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-widest text-[#00D4FF] mb-3">{t("Indicative pricing")}</div>
          <h2 className="font-heading text-3xl text-graphite">{t("What things typically cost.")}</h2>
          <div data-testid="products-pricing-table" className="mt-8 rounded-2xl border border-slate-100 overflow-hidden shadow-card-soft">
            {PRICING.rows.map(([item, price], i) => (
              <div key={item} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-6 py-4 ${i % 2 ? "bg-mist" : "bg-white"}`}>
                <span className="text-sm text-slate-700">{t(item)}</span>
                <span className="text-sm font-medium text-graphite">{t(price)}</span>
              </div>
            ))}
          </div>
          <p data-testid="products-pricing-disclaimer" className="mt-4 text-xs text-slate-500">{t(PRICING.disclaimer)}</p>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-[#071A2D] text-white py-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="font-heading text-3xl">{t("Not sure where to start? Start with data.")}</h3>
          <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="products-bottom-cta" className="mt-6 inline-flex items-center gap-2 btn-gradient px-6 py-3 rounded-full text-sm">
            {t("Start with a Free EnergyScan")} <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
